import { Box, Flex, Input, Menu as ChakraMenu, Portal } from '@chakra-ui/react';
import { Search } from 'lucide-react';
import * as React from 'react';

import {
  menuContentPaddingLg,
  menuItemDestructiveColor,
  menuItemDestructiveHighlightedBg,
  menuSearchIconColor,
  menuSearchIconSize,
  menuSearchIconStrokeWidth,
  menuSearchPaddingY,
  menuSearchTextColor,
  menuVariant,
} from './menu.kds';

/** KDS menu — `variant` and `size` are fixed, not configurable. */
export type MenuRootProps = Omit<ChakraMenu.RootProps, 'variant' | 'size'>;
export type MenuTriggerProps = ChakraMenu.TriggerProps;
export type MenuContentProps = ChakraMenu.ContentProps & {
  /** Props forwarded to the internal `Positioner` wrapper. */
  positionerProps?: ChakraMenu.PositionerProps;
  /**
   * Render the popup in a portal so it escapes overflow/scroll containers
   * (e.g. table scroll areas). Defaults to `true` to match Modal/Drawer/Select.
   * Pass `false` only when the menu must stay in the local DOM tree.
   */
  portalled?: boolean;
  /** Portal container (defaults to `document.body`). */
  portalRef?: React.RefObject<HTMLElement | null>;
};

export interface MenuItemProps extends ChakraMenu.ItemProps {
  /** Leading icon slot. */
  startIcon?: React.ReactNode;
  /** Trailing icon slot. */
  endIcon?: React.ReactNode;
  /**
   * Destructive action styling — `red.50` background + `red.700` text.
   * Highlight stays on Red/50 (does not switch to brand.50).
   */
  destructive?: boolean;
}

export interface MenuItemGroupProps extends Omit<ChakraMenu.ItemGroupProps, 'title'> {
  /** Section heading (uppercase small caps). */
  title?: React.ReactNode;
}

export interface MenuSearchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Search field placeholder (default: `Search`). */
  placeholder?: string;
}

function stopMenuEventPropagation<T extends React.SyntheticEvent>(handler?: (event: T) => void) {
  return (event: T) => {
    event.stopPropagation();
    handler?.(event);
  };
}

function MenuRoot(props: Readonly<MenuRootProps>) {
  return <ChakraMenu.Root lazyMount unmountOnExit size="md" variant={menuVariant} {...props} />;
}
MenuRoot.displayName = 'MenuRoot';

const MenuTrigger = ChakraMenu.Trigger;

const MenuContent = React.forwardRef<HTMLDivElement, MenuContentProps>(function MenuContent(props, ref) {
  const { positionerProps, portalled = true, portalRef, ...rest } = props;
  return (
    <Portal disabled={!portalled} container={portalRef}>
      <ChakraMenu.Positioner {...positionerProps}>
        <ChakraMenu.Content ref={ref} {...rest} />
      </ChakraMenu.Positioner>
    </Portal>
  );
});

const MenuItem = React.forwardRef<HTMLDivElement, MenuItemProps>(function MenuItem(props, ref) {
  const { startIcon, endIcon, children, destructive = false, ...rest } = props;

  return (
    <ChakraMenu.Item
      ref={ref}
      width="100%"
      data-destructive={destructive || undefined}
      {...rest}
      {...(destructive
        ? {
            color: menuItemDestructiveColor,
            _highlighted: {
              bg: menuItemDestructiveHighlightedBg,
              color: menuItemDestructiveColor,
            },
            css: {
              '& svg': {
                color: menuItemDestructiveColor,
              },
            },
          }
        : {})}
    >
      {startIcon && (
        <Box data-menu-item-start-icon flexShrink={0}>
          {startIcon}
        </Box>
      )}
      <Box data-menu-item-label data-testid="menu-item-label" flex="1" minW={0}>
        {children}
      </Box>
      {endIcon && (
        <Box data-menu-item-end-icon data-testid="menu-item-end-icon" flexShrink={0}>
          {endIcon}
        </Box>
      )}
    </ChakraMenu.Item>
  );
});
MenuItem.displayName = 'MenuItem';

const MenuSearch = React.forwardRef<HTMLInputElement, MenuSearchProps>(function MenuSearch(props, ref) {
  const { placeholder = 'Search', onPointerDown, onClick, onKeyDown, ...rest } = props;

  return (
    <Flex
      align="center"
      borderBottomWidth="1px"
      borderColor="neutral.100"
      data-menu-search
      gap="2"
      mt={`-${menuContentPaddingLg}`}
      mx={`-${menuContentPaddingLg}`}
      px={menuContentPaddingLg}
      py={menuSearchPaddingY}
      onPointerDown={stopMenuEventPropagation()}
      onClick={stopMenuEventPropagation()}
    >
      <Box color={menuSearchIconColor} flexShrink={0} lineHeight={0}>
        <Search aria-hidden size={menuSearchIconSize} strokeWidth={menuSearchIconStrokeWidth} />
      </Box>
      <Input
        ref={ref}
        unstyled
        bg="transparent"
        border="none"
        color={menuSearchTextColor}
        flex="1"
        minW={0}
        outline="none"
        placeholder={placeholder}
        textStyle="default.regular"
        type="search"
        _placeholder={{ color: menuSearchIconColor }}
        onPointerDown={stopMenuEventPropagation(onPointerDown)}
        onClick={stopMenuEventPropagation(onClick)}
        onKeyDown={stopMenuEventPropagation(onKeyDown)}
        {...rest}
      />
    </Flex>
  );
});
MenuSearch.displayName = 'MenuSearch';

const MenuItemGroup = React.forwardRef<HTMLDivElement, MenuItemGroupProps>(function MenuItemGroup(props, ref) {
  const { title, children, ...rest } = props;

  return (
    <ChakraMenu.ItemGroup ref={ref} {...rest}>
      {title != null && <ChakraMenu.ItemGroupLabel userSelect="none">{title}</ChakraMenu.ItemGroupLabel>}
      {children}
    </ChakraMenu.ItemGroup>
  );
});
MenuItemGroup.displayName = 'MenuItemGroup';

export { MenuContent, MenuItem, MenuItemGroup, MenuRoot, MenuSearch, MenuTrigger };

export const Menu = {
  Root: MenuRoot,
  Trigger: MenuTrigger,
  Content: MenuContent,
  Search: MenuSearch,
  Item: MenuItem,
  ItemGroup: MenuItemGroup,
};
